import { isValidSuiAddress } from '@mysten/sui/utils';
import bs58 from 'bs58';
import type { Address } from 'viem';
import { getAddress } from 'viem';

import { type ChainKey, type NearIntentsAddress } from '@infinex/chain-config';

import {
  type BitcoinAddress,
  type CardanoAddress,
  type DogecoinAddress,
  isNearIntentsTokenId,
  isValidNearAddress,
  isValidNearIntentsAddress,
  type LitecoinAddress,
  type NearAccount,
  type XrpAddress,
  type ZecAddress,
} from './address';
import type { NftMetadata } from './balances';
import type { ChainAddressId, ChainAssetId } from './chainAssets';
import type { MarketDataProviderType } from './marketDataProvider';
import type { SocialLinks } from './socials';
import { splitOnFirstUnderscore } from './utils';

// Used for column lengths mostly
export const MAX_SYMBOL_LENGTH = 80;
export const MAX_TOKEN_NAME_LENGTH = 150;
export const MAX_ASSET_ID_LENGTH = 100;

export type AssetId = string;
export type AssetSymbol = string;
export type AssetType = 'token' | 'nft';

// Type alias for SUI addresses (both plain and token addresses)
export type SuiAddress = `0x${string}` | `0x${string}::${string}::${string}`;

// Type alias for Solana addresses (base58-encoded public keys)
export type SolanaAddress = string;

export type CustodyChainAddress =
  | SolanaAddress
  | Address
  | NearIntentsAddress
  | NearAccount
  | SuiAddress
  | BitcoinAddress
  | CardanoAddress
  | DogecoinAddress
  | LitecoinAddress
  | XrpAddress
  | ZecAddress;

export type ChainAddress = CustodyChainAddress | 'native';

export function parseChainAddress(value: string): ChainAddress {
  if (value === 'native') {
    return value;
  }

  const strValue = value.toString();

  // Fast path: Most common case - EVM addresses
  if (strValue.startsWith('0x') && strValue.length === 42) {
    try {
      // Try to checksum the address - this will throw if it's not a valid hex address
      return getAddress(strValue);
    } catch {
      // If not a valid EVM address, continue to other address types
    }
  }

  // Handle SUI addresses (both token and plain)
  if (strValue.startsWith('0x')) {
    // SUI token addresses (0x...::[token name]::[token ticker])
    if (strValue.includes('::')) {
      const baseAddress = strValue.split('::')[0];
      // Allow system addresses like 0x2, 0x1, etc. or validate full SUI addresses
      const isSystemAddress = /^0x[0-9a-fA-F]{1,3}$/.test(baseAddress);
      if (!isSystemAddress && !isValidSuiAddress(baseAddress)) {
        throw new Error('Invalid SUI token address: ' + value);
      }
      return strValue as `0x${string}::${string}::${string}`;
    }

    // Plain SUI addresses
    if (isValidSuiAddress(strValue)) {
      return strValue as `0x${string}`;
    }
  }

  // Handle Solana addresses
  if (isValidSolanaAddress(strValue)) {
    return strValue;
  }

  // NEAR Intents token ids: `nep141:<contract>` or `nep245:<contract>:<id>`
  if (isNearIntentsTokenId(strValue)) {
    return strValue as NearIntentsAddress;
  }

  // Handle NEAR Intents chain addresses (ex. BTC, XRP, DOGE)
  if (isValidNearIntentsAddress(strValue)) return strValue;

  // Handle NEAR addresses (implicit 64-char hex, named like "alice.near", Ethereum-like 0x...)
  // Intentionally placed at the bottom here since it's probably the most broad and unrestricted
  if (isValidNearAddress(strValue)) return strValue;

  throw new Error('Invalid address: ' + value);
}

export function isValidChainAddress(value: string): boolean {
  try {
    parseChainAddress(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Compare two addresses by canonical form (see {@link parseChainAddress}):
 * EVM addresses match checksum-insensitively, everything else — including the
 * `'native'` sentinel — matches exactly. Never throws; unparseable input
 * matches nothing.
 */
export function isChainAddressEqual(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    return parseChainAddress(a) === parseChainAddress(b);
  } catch {
    return false;
  }
}

/**
 * Validates and parses a token address, throwing a standardized error if invalid.
 *
 * This is a higher-level validation function that wraps parseChainAddress with
 * consistent error handling for API operations.
 *
 * @param tokenAddress - Token address string to validate
 * @returns Object with the parsed and normalized token address
 * @throws Error with message 'Invalid token address' if address is invalid
 *
 * @example
 * ```typescript
 * const { tokenAddress } = validateTokenAddress('0x...');
 * // Use in Promise.all for parallel validation
 * const [userData, { tokenAddress }] = await Promise.all([
 *   getUserData(),
 *   validateTokenAddress(input.tokenAddress),
 * ]);
 * ```
 */
export function validateTokenAddress(tokenAddress: string): {
  tokenAddress: string;
} {
  try {
    const parsedTokenAddress = parseChainAddress(tokenAddress).toString();
    return { tokenAddress: parsedTokenAddress };
  } catch {
    throw new Error('Invalid token address');
  }
}

/**
 * Checks if a string is a valid SUI address (plain or token address)
 * @param address - The address to check
 * @returns true if it's a valid SUI address
 */
export function isSuiAddress(address: string): boolean {
  if (address === 'native') return true;

  // Check for SUI token addresses (0x...::[module]::[type])
  if (address.startsWith('0x') && address.includes('::')) {
    const baseAddress = address.split('::')[0];
    // Allow system addresses like 0x2, 0x1, etc. or validate full SUI addresses
    const isSystemAddress = /^0x[0-9a-fA-F]{1,3}$/.test(baseAddress);
    return isSystemAddress || isValidSuiAddress(baseAddress);
  }

  // Check for regular SUI addresses
  return isValidSuiAddress(address);
}

export function makeChainAddressId(
  chain: ChainKey,
  address: string
): ChainAddressId {
  return `${chain}_${parseChainAddress(address).toString()}` as ChainAddressId;
}

/**
 * @example getAddressFromChainAddressId('nearIntents_nep141:xrp.omft.near') === 'nep141:xrp.omft.near'
 */
export function getAddressFromChainAddressId(
  chainAddressId: ChainAddressId
): string {
  return splitOnFirstUnderscore(chainAddressId)[1] as string;
}

/**
 * @example getAssetIdFromChainAssetId('nearIntents_bitcoin') === 'bitcoin'
 */
export function getAssetIdFromChainAssetId(
  chainAssetId: ChainAssetId
): AssetId {
  return splitOnFirstUnderscore(chainAssetId)[1] as AssetId;
}

/** The data which is consistent across all the chain deployments/members for an asset in the official list */
export type OfficialAsset = {
  id: AssetId;
  name: string;
  type: AssetType;
  symbol: AssetSymbol;
  coingeckoId: string | null;
  imgUrl: string | null;
};

export type OfficialAssetsByAssetId = Record<AssetId, OfficialAsset>;

export const STABLE_ASSET_IDS: AssetId[] = [
  'binance_bridged_usdc_bnb_smart_chain',
  'binance_bridged_usdt_bnb_smart_chain',
  'dai',
  'ethena_staked_usde',
  'l2_standard_bridged_usdt_base',
  'nusd',
  'staked_frax_usd',
  'susds',
  'usd_blast',
  'usd_coin_base_bridged',
  'usd_coin_ethereum_bridged',
  'usd_coin',
  'usd_ethena',
  'usd_tether',
  'usd1_wlfi',
  'usds',
  'usdt0',
];

export const INX_METADATA = {
  name: 'INX',
  imageUrl:
    'https://res.cloudinary.com/infinex/image/upload/tokens/infinex.svg',
};

export const SHARED_PATRON_ASSET_METADATA = {
  name: 'Infinex Patron',
  imageUrl: 'https://infinex.xyz/tokens/patron_nft.svg',
};

export type NftDisplayMetadataMap = {
  identifiers: string[];
  metadataMap?: {
    [identifier: string]: {
      name: string;
      imageUrl?: string;
    };
  };
};

export const createNftDisplayMetadataMap = (
  nftMetadataArray: NftMetadata[]
): NftDisplayMetadataMap => {
  return {
    identifiers: nftMetadataArray.map((nft) => nft.identifier),
    metadataMap: Object.fromEntries(
      nftMetadataArray.map((nft) => [
        nft.identifier,
        {
          name: nft.name ?? 'Unknown',
          imageUrl: nft.imageUrl,
        },
      ])
    ),
  };
};

export const MAX_PATRON_WITHDRAWAL_AMOUNT = 50;

export type NftStandard = 'erc721' | 'erc721a' | 'crypto_punks' | 'ether_rocks';

export type LstAssetMetadata = {
  // Identifiers
  address: string;
  chain: ChainKey;
  assetId: AssetId;
  // Pool metadata from DefiLlama
  symbol: string;
  project: string;
  tvlUsd: number;
  apyBase: number | null;
  apyReward: number | null;
  apy: number;
  rewardTokens: (string | null)[] | null;
  pool: string;
  apyPct1D: number | null;
  apyPct7D: number | null;
  apyPct30D: number | null;
  stablecoin: boolean;
  ilRisk: string;
  exposure: string;
  predictions: {
    predictedClass: string | null;
    predictedProbability: number | null;
    binnedConfidence: number | null;
  };
  poolMeta: string | null;
  mu: number;
  sigma: number;
  count: number;
  outlier: boolean;
  underlyingTokens: (string | null)[] | null;
  il7d: number | null;
  apyBase7d: number | null;
  apyMean30d: number | null;
  volumeUsd1d: number | null;
  volumeUsd7d: number | null;
  apyBaseInception: number | null;
  // Protocol metadata from DefiLlama
  name: string;
  slug: string;
  category: string;
  logo: string;
  description: string | null;
};

export type LstMetadataByAssetId = Record<AssetId, LstAssetMetadata>;

export type GroupAssetMarketData = {
  chain: ChainKey;
  address: string;
  // TODO: separate price from other market data?
  rate: {
    value: number | null;
    /** In seconds */
    lastUpdatedTs: number | null;
  };
  marketCap: number | null;
  fdv: number | null;
  volume24h: number | null;
  circulatingSupply: number | null;
  totalSupply: number | null;
  maxSupply: number | null;
  rank: number | null;
  priceChange24h: number | null;
  sparkline7d: {
    price: number[];
  } | null;
  /** Present when sourced from a chain member (e.g. Codex); group-only providers omit it. */
  liquidity?: number | null;
};

export type ChainAssetMarketData = {
  chain: ChainKey;
  address: string;
  rate: {
    value: number | null;
    /** In seconds */
    lastUpdatedTs: number | null;
  };
  marketCap: number | null;
  fdv: number | null;
  volume24h: number | null;
  circulatingSupply: number | null;
  totalSupply: number | null;
  maxSupply: null; // TODO: not given by Codex
  liquidity: number | null;
  holders: number | null;
  priceChange24h: number | null;
};

export type ChartLookback = '1D' | '1W' | '1M' | '1Y' | 'MAX';

export function chartLookbackToDays(lookback: ChartLookback): number | 'max' {
  switch (lookback) {
    case 'MAX':
      return 'max';
    case '1Y':
      return 365;
    case '1M':
      return 30;
    case '1W':
      return 7;
    case '1D':
      return 1;
  }
}

export type LiquidityResult = {
  chain: ChainKey;
  address: string;
  liquidity: number | null;
  source: MarketDataProviderType;
};

export type PriceResult = {
  priceUsd: number | null;
  change24h: number | null;
  marketCap: number | null;
  volume24h: number | null;
  lastUpdated: Date;
  source: MarketDataProviderType;
};

export type TokenSearchResult = {
  results: Array<{
    groupId: { chain: ChainKey; address: string };
    members: Partial<
      Record<
        ChainKey,
        {
          id: ChainAddressId;
          chain: ChainKey;
          address: string;
          decimals: number;
          imgUrl: string;
          type: 'token' | 'nft';
          name: string;
          symbol: string;
        }
      >
    >;
    type: 'token' | 'nft';
    name: string;
    symbol: string;
    imgUrl: string;
    marketData?: TokenMarketData;
  }>;
  count: number;
};

export type TokenMarketData = {
  priceUSD: number | null;
  marketCap: number | null;
  liquidity: number | null;
  holders: number | null;
  volume24h: number | null;
  volume1h?: number | null;
  volume4h?: number | null;
  volume12h?: number | null;
  change24h: number | null; // percentage, e.g. 12.34 = 12.34%
  change5m?: number | null; // percentage, e.g. 12.34 = 12.34%
  createdAt?: number | null; // token creation, unix seconds
  change1h?: number | null; // percentage, e.g. 12.34 = 12.34%
  change4h?: number | null; // percentage, e.g. 12.34 = 12.34%
  change12h?: number | null; // percentage, e.g. 12.34 = 12.34%
  isScam: boolean | null;
};

/**
 * Validates a Solana address by attempting to decode it as base58
 * and checking if it's 32 bytes (standard Solana public key length).
 */
export function isValidSolanaAddress(address: string): boolean {
  try {
    const decoded = bs58.decode(address);
    return decoded.length === 32;
  } catch {
    return false;
  }
}

export type TokenMetadataResult = {
  chain: ChainKey;
  address: string;
  name: string | null;
  symbol: string | null;
  decimals: number | null;
  imgUrl: string | null;
  coingeckoId: string | null;
  isScam: boolean | null;
  socials?: SocialLinks;
};

export type HistoricalPriceResult = {
  priceUsd: number;
  date: Date | null;
};

export type HistoricalPriceKey = `${ChainKey}-${string}-${number}`; // `${chain}-${address}-${timestamp}`
export type HistoricalPriceMap = Record<
  HistoricalPriceKey,
  HistoricalPriceResult | undefined
>;
